import { useMemo, useCallback } from "react";
import { useStartupStore } from "../modules/startup/stores";
import type { RedirectConfigItem } from "../modules/homeDisplay/types";
import {
  buildDisplayIconMap,
  resolveFlagSrcByCode,
  resolveFlagSrcByItem,
  getCountryCodeFromId,
  getCountryCodeForFlag,
} from "../utils/countryFlag";

/**
 * 国家国旗 hook：自动订阅 startup.countries，对外暴露统一的解析方法。
 * - getFlagSrcByCode(code)：基于两位国家码（小写）解析
 * - getFlagSrcByItem(item)：基于 RedirectConfigItem 解析
 * - getFlagSrcByCountryId(id)：基于业务国家 ID（如 PH001/IDN/US）解析
 * 优先 displayIcon，回退 flag-icons CDN，没有 code 时返回空串走占位符。
 */
export function useCountryFlag() {
  const startupCountries = useStartupStore(s => s.startupData?.countries);

  const displayIconMap = useMemo(
    () => buildDisplayIconMap(startupCountries),
    [startupCountries]
  );

  const getFlagSrcByCode = useCallback(
    (code: string | null | undefined) =>
      resolveFlagSrcByCode(code, displayIconMap),
    [displayIconMap]
  );

  const getFlagSrcByItem = useCallback(
    (item: RedirectConfigItem) => resolveFlagSrcByItem(item, displayIconMap),
    [displayIconMap]
  );

  const getFlagSrcByCountryId = useCallback(
    (countryId: string | null | undefined) =>
      resolveFlagSrcByCode(getCountryCodeFromId(countryId), displayIconMap),
    [displayIconMap]
  );

  return {
    displayIconMap,
    getFlagSrcByCode,
    getFlagSrcByItem,
    getFlagSrcByCountryId,
    getCountryCodeFromId,
    getCountryCodeForFlag,
  };
}
